// Комментарий на русском: локальный кэш голосовых в браузере через Cache Storage.
// Зачем: офлайн-воспроизведение, не дёргаем сервер повторно, удаление при синхронизации.

import { getAccessToken } from '@/lib/authStorage';
import { API_URL } from '@/lib/api';

// Комментарий на русском:
// v3: сброс после инцидентов с “битым” кэшем (усечённые файлы при обрыве сети).
// Это позволяет не застревать на старых blob-ответах у пользователей (CacheStorage живёт долго).
const VOICE_CACHE_NAME = 'lisad_voice_files_v3';

// Комментарий на русском: приводим относительный путь голосового к абсолютному URL.
// Зачем: в проде сайт может открываться как www/non-www, а переменная NEXT_PUBLIC_API_URL может быть другой.
// Для /uploads/* важно использовать текущий origin страницы (иначе fetch для кэша упирается в CORS).
// В local_test Next.js route handler сам проксирует /uploads/ на PHP (:8080), поэтому тоже используем origin.
export function resolveVoiceUrl(src: string): string {
  const u = String(src || '').trim();
  if (!u || u.startsWith('blob:') || u.startsWith('data:') || /^https?:\/\//.test(u)) return u;
  if (!u.startsWith('/')) return u;

  // Комментарий на русском: для /uploads/* и /media/* всегда используем origin текущей страницы.
  // Зачем: в local_test Next.js route handler проксирует на PHP :8080, в проде — читает с диска.
  const isUploads = u.startsWith('/uploads/') || u.startsWith('/media/');
  if (isUploads && typeof window !== 'undefined') {
    return `${window.location.origin}${u}`;
  }

  return `${API_URL}${u}`;
}

// Комментарий на русском: определяем платформу для выбора оптимального формата аудио.
export type AudioPlatform = 'ios' | 'android' | 'chrome' | 'safari' | 'firefox' | 'edge' | 'unknown';

export function detectAudioPlatform(): AudioPlatform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Edg/i.test(ua)) return 'edge';
  if (/Chrome/i.test(ua)) return 'chrome';
  if (/Safari/i.test(ua)) return 'safari';
  if (/Firefox/i.test(ua)) return 'firefox';
  return 'unknown';
}

// Комментарий на русском: оптимальные форматы по платформам.
// iOS Safari: mp4/aac (webm не поддерживается)
// Android Chrome: webm/opus (лучшее сжатие)
// Desktop Chrome/Edge: webm/opus
// Firefox: ogg/opus или webm/opus
// Safari macOS: mp4/aac
export function getPreferredAudioMime(platform?: AudioPlatform): string {
  const p = platform || detectAudioPlatform();
  switch (p) {
    case 'ios':
    case 'safari':
      return 'audio/mp4';
    case 'firefox':
      return 'audio/ogg';
    default:
      return 'audio/webm';
  }
}

// Комментарий на русском: проверяем доступность Cache Storage.
function canUseCache(): boolean {
  return typeof window !== 'undefined' && typeof caches !== 'undefined' && typeof caches.open === 'function';
}

function isSkippableSrc(src: string): boolean {
  return !src || src.startsWith('blob:') || src.startsWith('data:');
}

function isBadContentType(ct: string): boolean {
  const v = String(ct || '').toLowerCase();
  return v.includes('text/html') || v.startsWith('text/') || v.includes('application/json');
}

function isLikelyAudioContentType(ct: string): boolean {
  const v = String(ct || '').toLowerCase();
  return v.startsWith('audio/') || v === 'application/octet-stream';
}

// Комментарий на русском: пытаемся угадать MIME по расширению файла в URL.
// Зачем: на некоторых серверах .webm/.m4a отдаются как video/* или application/octet-stream, а blob: с таким типом может не играть в <audio>.
function guessAudioMimeFromUrl(url: string): string {
  const u = String(url || '').toLowerCase();
  const m = u.match(/\.([a-z0-9]+)(?:\?|#|$)/);
  const ext = (m?.[1] || '').toLowerCase();
  switch (ext) {
    case 'webm':
      return 'audio/webm';
    case 'ogg':
    case 'oga':
      return 'audio/ogg';
    case 'm4a':
    case 'mp4':
      return 'audio/mp4';
    case 'mp3':
      return 'audio/mpeg';
    case 'wav':
      return 'audio/wav';
    default:
      return '';
  }
}

// Комментарий на русском: нормализуем Content-Type до "аудио" (если можем).
function normalizeAudioMime(ct: string, url: string, hint?: string): string {
  const h = String(hint || '').trim().toLowerCase();
  // Комментарий на русском:
  // Раньше мы безусловно доверяли hint (voice_mime) — но при редиректах/прокси сервер может отдать другой формат
  // (например, iOS просит mp4, а исходник был webm). Если оставить hint приоритетным, получаем Blob с неверным MIME
  // и <audio> падает с MEDIA_ERR_DECODE (3).
  // Поэтому: сначала доверяем реальному Content-Type (если это audio/* или video/*), а hint используем как fallback,
  // когда сервер отдаёт application/octet-stream или пустой тип.

  const v0 = String(ct || '').trim().toLowerCase();
  const v = v0.includes(';') ? v0.split(';', 1)[0].trim() : v0;
  let vv = v;
  if (vv === 'video/webm') vv = 'audio/webm';
  if (vv === 'video/mp4') vv = 'audio/mp4';
  if (vv === 'audio/x-m4a') vv = 'audio/mp4';

  // 1) Если сервер уже сказал audio/* — используем это (самый надёжный источник).
  if (vv.startsWith('audio/')) return vv;

  // 2) Если сервер дал общий/пустой тип — используем hint или расширение.
  if (!vv || vv === 'application/octet-stream') {
  if (h.startsWith('audio/')) return h;
    const g2 = guessAudioMimeFromUrl(url);
    if (g2) return g2;
    return 'audio/webm';
  }

  // 3) Неизвестный тип (но не "bad" — это отфильтровано выше). Пробуем расширение/подсказку.
  const g = guessAudioMimeFromUrl(url);
  if (g) return g;
  if (h.startsWith('audio/')) return h;

  // Комментарий на русском: последнее приближение — не даём пустоту.
  return vv || 'audio/webm';
}

// Комментарий на русском: "перетипизируем" blob без копирования байт (обёртка Blob -> Blob).
function fixBlobMime(blob: Blob, mime: string): Blob {
  const m = String(mime || '').trim().toLowerCase();
  if (!m) return blob;
  const bt = String(blob?.type || '').trim().toLowerCase();
  if (bt === m) return blob;
  return new Blob([blob], { type: m });
}

// проверяем есть ли файл в кэше (без загрузки blob).
export async function isVoiceCached(src: string): Promise<boolean> {
  try {
    const u = String(src || '').trim();
    if (!canUseCache() || isSkippableSrc(u)) return false;
    const cache = await caches.open(VOICE_CACHE_NAME);
    const res = await cache.match(u);
    return !!(res && res.ok);
  } catch {
    return false;
  }
}

// получаем blob из кэша.
export async function getCachedVoiceBlob(src: string, mimeHint?: string): Promise<Blob | null> {
  try {
    const u = String(src || '').trim();
    if (!canUseCache() || isSkippableSrc(u)) return null;
    const cache = await caches.open(VOICE_CACHE_NAME);
    const res = await cache.match(u);
    if (!res || !res.ok) return null;
    const ct = res.headers.get('content-type') || '';
    if (isBadContentType(ct)) {
      await cache.delete(u);
      return null;
    }
    const desired = normalizeAudioMime(ct, u, mimeHint);
    const b0 = await res.blob();
    const b = desired ? fixBlobMime(b0, desired) : b0;
    // Комментарий на русском: если в кэше лежит "плохой" Content-Type — переписываем, чтобы <audio> играл стабильно.
    if (desired && desired !== String(ct || '').trim().toLowerCase()) {
      try {
        await cache.put(u, new Response(b, { headers: { 'Content-Type': desired } }));
      } catch {
        // ignore
      }
    }
    return b;
  } catch {
    return null;
  }
}

// Комментарий на русском: кладём blob в кэш под URL.
export async function putVoiceBlobToCache(src: string, blob: Blob, mime?: string): Promise<void> {
  try {
    const u = String(src || '').trim();
    if (!canUseCache() || isSkippableSrc(u) || !blob) return;
    const cache = await caches.open(VOICE_CACHE_NAME);
    const existing = await cache.match(u);
    if (existing && existing.ok) return;
    const headers = new Headers();
    const ct0 = String(mime || blob.type || '').trim();
    const ct = normalizeAudioMime(ct0, u);
    if (ct) headers.set('Content-Type', ct);
    await cache.put(u, new Response(blob, { headers }));
  } catch {
    // ignore
  }
}

// Комментарий на русском: колбэк для отслеживания прогресса загрузки.
export type DownloadProgressCallback = (loaded: number, total: number | null) => void;

// Комментарий на русском: загружаем файл в кэш с отслеживанием прогресса (как в Telegram).
export async function downloadVoiceToCache(
  src: string,
  onProgress?: DownloadProgressCallback,
  opts?: { signal?: AbortSignal; mimeHint?: string }
): Promise<{ success: boolean; blob: Blob | null; error?: string }> {
  try {
    const u = String(src || '').trim();
    if (!u || isSkippableSrc(u)) {
      return { success: false, blob: null, error: 'invalid_src' };
    }

    // Комментарий на русском: сначала проверяем кэш.
    if (canUseCache()) {
      const cached = await getCachedVoiceBlob(u, opts?.mimeHint);
      if (cached) {
        onProgress?.(cached.size, cached.size);
        return { success: true, blob: cached };
      }
    }

    const token = getAccessToken() || '';
    // Комментарий на русском: предпочитаемый формат зависит от платформы (iOS: mp4, Android/Chrome: webm, Firefox: ogg).
    const pref = getPreferredAudioMime(detectAudioPlatform());
    const headers: Record<string, string> = { Accept: `${pref},audio/*;q=0.8,*/*;q=0.1` };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(u, { method: 'GET', credentials: 'include', headers, signal: opts?.signal });
    if (!res || !res.ok) {
      return { success: false, blob: null, error: `http_${res?.status || 0}` };
    }

    const ct = res.headers.get('content-type') || '';
    if (isBadContentType(ct)) {
      return { success: false, blob: null, error: 'bad_content_type' };
    }
    const mime = normalizeAudioMime(ct, u, opts?.mimeHint);

    // Комментарий на русском: читаем с прогрессом через ReadableStream.
    const contentLength = res.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : null;
    const reader = res.body?.getReader();

    if (!reader) {
      // Комментарий на русском: fallback если ReadableStream недоступен.
      const b0 = await res.blob();
      const blob = mime ? fixBlobMime(b0, mime) : b0;
      if (canUseCache()) {
        await putVoiceBlobToCache(u, blob, mime);
      }
      onProgress?.(blob.size, blob.size);
      return { success: true, blob };
    }

    const chunks: BlobPart[] = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        // Комментарий на русском: копируем в новый ArrayBuffer для совместимости с Blob.
        chunks.push(new Uint8Array(value).buffer);
        loaded += value.length;
        onProgress?.(loaded, total);
      }
    }

    // Комментарий на русском:
    // При обрыве связи/прокси иногда поток может завершиться "done=true" раньше, чем придут все байты.
    // Тогда мы получаем усечённый файл, который попадает в CacheStorage и позже ломает <audio> (errorCode=4/3).
    // Если сервер дал Content-Length — проверяем соответствие.
    if (total !== null && Number.isFinite(total) && total > 0 && loaded > 0 && loaded < total) {
      return { success: false, blob: null, error: 'incomplete_download' };
    }

    const blob = new Blob(chunks, { type: mime || ct || 'audio/webm' });

    // Комментарий на русском: сохраняем в кэш для офлайн-доступа.
    if (canUseCache()) {
      await putVoiceBlobToCache(u, blob, mime);
    }

    return { success: true, blob };
  } catch (e: any) {
    return { success: false, blob: null, error: e?.message || 'network_error' };
  }
}

// Комментарий на русском: простая загрузка без прогресса (совместимость).
export async function ensureVoiceCached(src: string): Promise<void> {
  await downloadVoiceToCache(src);
}

// Комментарий на русском: удаляем файл из кэша.
export async function deleteCachedVoice(src: string): Promise<void> {
  try {
    const u = String(src || '').trim();
    if (!canUseCache() || isSkippableSrc(u)) return;
    const cache = await caches.open(VOICE_CACHE_NAME);
    await cache.delete(u);
  } catch {
    // ignore
  }
}

// Комментарий на русском: удаляем файлы по списку id сообщений (для синхронизации удалений с сервера).
export async function deleteCachedVoicesByMessageIds(
  messageIds: number[],
  urlMap: Map<number, string>
): Promise<void> {
  if (!canUseCache() || !messageIds.length) return;
  try {
    const cache = await caches.open(VOICE_CACHE_NAME);
    for (const id of messageIds) {
      const url = urlMap.get(id);
      if (url) {
        await cache.delete(url);
      }
    }
  } catch {
    // ignore
  }
}

// Комментарий на русском: получаем список всех URL в кэше.
export async function listCachedVoiceUrls(): Promise<string[]> {
  try {
    if (!canUseCache()) return [];
    const cache = await caches.open(VOICE_CACHE_NAME);
    const keys = await cache.keys();
    return keys.map((r) => r.url);
  } catch {
    return [];
  }
}

// Комментарий на русском: очищаем весь кэш голосовых.
export async function purgeVoiceCache(): Promise<void> {
  try {
    if (canUseCache()) {
      const keys = await caches.keys();
      for (const k of keys) {
        if (k.startsWith('lisad_voice_')) {
          await caches.delete(k);
        }
      }
    }
    if (typeof window !== 'undefined') {
      try {
        const toDelete: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i) || '';
          if (
            key.startsWith('lisad_voice_wave_index') ||
            key.startsWith('lisad_voice_wave_v') ||
            key.startsWith('lisad_voice_seen_v')
          ) {
            toDelete.push(key);
          }
        }
        toDelete.forEach((k) => localStorage.removeItem(k));
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }
}

// Комментарий на русском: проверяем онлайн/офлайн статус.
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
}

// Комментарий на русском: создаём blob URL из кэша для воспроизведения.
export async function getPlayableVoiceUrl(src: string): Promise<string | null> {
  const blob = await getCachedVoiceBlob(src);
  if (!blob) return null;
  return URL.createObjectURL(blob);
}
